
import { useMemo } from 'react';
import { QuestionarioItem, PhotoQuestionarioItem } from '../../types';

export interface FotoIndividual {
  id: string; 
  questionId: string;
  pergunta: string;
  photo: {
    fileName: string;
    fileUrl: string;
    caption?: string;
  };
}

export function usePhotoFilter(questionario: QuestionarioItem[], photoOrder?: string[]) {
  const todasFotos = useMemo(() => {
    const fotos: FotoIndividual[] = [];

    questionario.forEach((item) => {
      if (item.answerType === 'Photo' && 'photos' in item) {
        const photoItem = item as PhotoQuestionarioItem;
        if (photoItem.photos && photoItem.photos.length > 0) {
          photoItem.photos.forEach((photo, photoIdx) => {
            const fotoId = `${item.id}-${photoIdx}`;
            fotos.push({
              id: fotoId,
              questionId: item.id,
              pergunta: item.pergunta,
              photo: {
                fileName: photo.fileName,
                fileUrl: photo.fileUrl,
                caption: photo.caption,
              },
            });
          });
        }
      }
    });

    return fotos;
  }, [questionario]);

  const fotosOrdenadas = useMemo(() => {
    if (!photoOrder || photoOrder.length === 0) {
      return todasFotos;
    }

    const fotosById = new Map(todasFotos.map(f => [f.id, f]));
    const ordenadas: FotoIndividual[] = [];

    photoOrder.forEach(id => {
      const foto = fotosById.get(id);
      if (foto) {
        ordenadas.push(foto);
        fotosById.delete(id);
      }
    });

    fotosById.forEach(foto => {
      ordenadas.push(foto);
    });

    return ordenadas;
  }, [todasFotos, photoOrder]);

  const itensComFotos = useMemo(() => {
    if (!photoOrder || photoOrder.length === 0) {
      return questionario.filter(
        (item): item is PhotoQuestionarioItem => {
          const isPhoto = item.answerType === 'Photo';
          const hasPhotos = isPhoto && 'photos' in item && (item as PhotoQuestionarioItem).photos && (item as PhotoQuestionarioItem).photos.length > 0;
          return isPhoto && hasPhotos;
        }
      );
    }

    return fotosOrdenadas.map(foto => ({
      id: foto.id,
      pergunta: foto.pergunta,
      answerType: 'Photo' as const,
      photos: [foto.photo],
    })) as PhotoQuestionarioItem[];
  }, [questionario, photoOrder, fotosOrdenadas]);

  const temFotos = todasFotos.length > 0;
  const totalFotos = todasFotos.length;

  return {
    itensComFotos,
    temFotos,
    totalFotos,
    fotosOrdenadas, 
  };
}
